import { Button, Card, CardContent, cn, Spinner } from '@crm/ui';
import {
  ArrowRight,
  Ban,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  PlayCircle,
  Plus,
  UserX,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '@/lib/utils';
import { AppointmentDialog } from '@/modules/schedule/components/appointment-dialog';
import { MANAGEMENT_ROUTES, type TimelineEvent, useTimeline } from '../api';

const ICONS: Record<TimelineEvent['kind'], React.ComponentType<{ className?: string }>> = {
  created: CalendarPlus,
  'checked-in': PlayCircle,
  completed: CheckCircle2,
  cancelled: Ban,
  'no-show': UserX,
};

const TONES: Record<TimelineEvent['kind'], string> = {
  created: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  'checked-in': 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  cancelled: 'bg-destructive/10 text-destructive',
  'no-show': 'bg-destructive/10 text-destructive',
};

/**
 * Atividades recentes da operacao. Cada linha leva ao agendamento de que
 * fala: a seta indica isso, e a modal abre por cima sem sair do painel.
 */
export function TimelineList({ limit = 15 }: { limit?: number }) {
  // "Ver mais" pede mais eventos e eles entram abaixo dos que ja estao na tela.
  const [size, setSize] = useState(limit);
  const timeline = useTimeline(size);
  const [openId, setOpenId] = useState<string | null>(null);

  const events = timeline.data?.data ?? [];
  // O endpoint devolve so a lista, sem total: veio cheia, pode ter mais.
  const mayHaveMore = events.length >= size;

  return (
    <Card className="-mx-3 flex h-full flex-col rounded-none border-x-0 bg-card sm:mx-0 sm:rounded-xl sm:border-x dark:bg-white/[0.06]">
      <CardContent className="flex-1 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold tracking-tight">Atividades recentes</h2>
          <Link
            to={MANAGEMENT_ROUTES.AUDIT}
            className="flex items-center gap-1 text-sm text-primary hover:underline"
          >
            Ver todas
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="-mx-4 sm:-mx-6">
          {timeline.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-5 w-5 text-muted-foreground" />
            </div>
          ) : events.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Nada por aqui ainda.</p>
          ) : (
            <ol className="divide-y divide-border">
              {events.map((event) => {
                const Icon = ICONS[event.kind];

                return (
                  <li key={event.id}>
                    <button
                      type="button"
                      onClick={() => setOpenId(event.appointmentId)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60 sm:px-5"
                    >
                      <span
                        className={cn(
                          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                          TONES[event.kind],
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-base font-medium">
                          {event.summary}
                        </span>
                        <span className="block truncate text-sm text-muted-foreground">
                          {formatDateTime(event.at)} - {event.unitName}
                          {event.professionalName ? ` - ${event.professionalName}` : ''}
                        </span>
                      </span>

                      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}

          {mayHaveMore && !timeline.isLoading ? (
            <div className="px-4 py-3 sm:px-5">
              <Button
                variant="ghost"
                className="w-full text-primary"
                disabled={timeline.isFetching}
                onClick={() => setSize((current) => current + limit)}
              >
                {timeline.isFetching ? <Spinner /> : <Plus className="h-4 w-4" />}
                Mostrar mais
              </Button>
            </div>
          ) : null}
        </div>

        {openId ? <AppointmentDialog id={openId} onClose={() => setOpenId(null)} /> : null}
      </CardContent>
    </Card>
  );
}
